import { verifyLogin } from 'util/cache'
import { useDocTitle } from 'util/hooks'
import { useUploadLessonMutation, useGenerateQuestionsMutation, useDeleteLessonMutation, useGetClassesQuery } from 'util/rtk-api'

import { DoneButton } from 'components/blue-button'
import { SearchBar } from 'components/search-bar'
import UploadBox from 'components/upload-box'
import { StudentTable } from 'components/tables/student-table'
import { DifficultyPills } from 'components/tables/difficulty-pills'
import { ClassContextProvider } from 'contexts/class-context'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import 'css/pages/upload-lessons.css'
import { LoadingModal } from 'components/loading-model'
import { showToast } from 'components/toast'
import { isValidUploadExtension } from 'util/upload-validation'
import { studentsMissingLevel } from 'util/student-levels'
import { MissingLevelModal } from 'components/missing-level-modal'
import { deriveGradeBand } from 'util/grade-band'

const VALID_PRESENTATION_TYPES = [
	'application/pdf',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation'
];

const VALID_SUPPLEMENTARY_TYPES = [
	...VALID_PRESENTATION_TYPES,
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'text/plain'
];

// Extensions, not MIME types, are what actually gate an upload (see below) --
// a file's browser-reported MIME type comes from the OS's file-type
// registration, which is unreliable for files that passed through Google
// Drive, email, or an older export tool. These match what the server
// validates against (api/src/endpoints/lessons) and what the upload box
// advertises to the user.
const PRESENTATION_EXTENSIONS = ['.pdf', '.ppt', '.pptx'];
const SUPPLEMENTARY_EXTENSIONS = [...PRESENTATION_EXTENSIONS, '.doc', '.docx', '.txt'];

const GeneratingModal = ({ fileName }: { fileName: string }) => (
	<LoadingModal>
		<div style={{ fontFamily: 'var(--font-body)', color: 'var(--umber)' }}>Generating questions for lesson:</div>
		<div style={{
			fontFamily: 'var(--font-heading)',
			wordBreak: 'break-word',
			maxWidth: '40ch',
			fontWeight: 600,
			color: 'var(--azure-deep)'
		}}>
			{fileName}
		</div>
	</LoadingModal>
);

export const UploadLessons = () => {
	const [presentationFile, setPresentationFile] = useState(null);
	const [lessonId, setLessonId] = useState(null);
	const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
	const [gradeBand, setGradeBand] = useState<string>('3-5');
	const [showGradeBandDropdown, setShowGradeBandDropdown] = useState(false);
	const [subject, setSubject] = useState<string>('');
	const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
	const [uploadBoxKey, setUploadBoxKey] = useState(0);
	const [currentTableDifficulty, setCurrentTableDifficulty] = useState('All');

	const navigate = useNavigate();

	// Use the RTK Query hooks
	const [uploadLesson, { isLoading: isUploading, error: uploadError }] = useUploadLessonMutation();
	const [generateQuestions, { isLoading: isGenerating }] = useGenerateQuestionsMutation();
	const [deleteLesson] = useDeleteLessonMutation();
	const { data: classesData } = useGetClassesQuery(undefined);

	const classes = [...(classesData?.classes || [])].sort((a, b) => a.name.localeCompare(b.name));
	const selectedClass = classes.find(c => c.classId === selectedClassId);
	// Blocking gate, moved here from lesson-launch (CEO, 8/22): catch a
	// missing level at the point a class gets assigned to a lesson, before
	// "Done" can be clicked -- not later when the lesson is actually launched.
	const missingLevelStudents = studentsMissingLevel(selectedClass?.students);

	// Update grade band when class is selected
	const handleClassSelect = (classId: string) => {
		setSelectedClassId(classId);
		const selected = classes.find(c => c.classId === classId);
		if (selected?.grades) {
			setGradeBand(deriveGradeBand(selected.grades));
		}
		setSubject(selected?.subject || '');
	};

	useDocTitle('All for One - Plan New Lesson');

	const handleFileUpload = async (file, isPresentation) => {
		if (!file) return;

		const validExtensions = isPresentation ? PRESENTATION_EXTENSIONS : SUPPLEMENTARY_EXTENSIONS;

		// Was validated against file.type (the browser's MIME guess) instead of
		// the extension. A real .pptx whose OS-reported type doesn't exactly
		// match the allowlist -- common for files that passed through Google
		// Drive or email -- silently failed here with no error shown, since a
		// bare `return` looks identical to "nothing selected".
		if (!isValidUploadExtension(file.name, validExtensions)) {
			showToast(`"${file.name}" isn't a supported file type. Accepted: ${validExtensions.join(', ')}`);
			return;
		}

		if (isPresentation) {
			setPresentationFile(file);
			try {
				// Use the RTK Query mutation
				const response = await uploadLesson(file).unwrap();
				if (response.lessonId) {
					setLessonId(response.lessonId);
				} else {
					setPresentationFile(null);
					showToast('Failed to upload lesson. Please try again.');
				}
			} catch (error) {
				// err-9: was console-only, leaving the file chip looking uploaded
				console.error('Error uploading lesson:', error);
				setPresentationFile(null);
				showToast('Failed to upload lesson. Please try again.');
			}
		} else {
			// TODO: Upload supplementary file
		}
	};

	const handleDoneClick = async () => {
		if (!selectedClassId) {
			showToast('Please select a class before generating questions.', 'info');
			return;
		}
		// Belt and suspenders alongside the blocking modal above -- don't rely
		// on the overlay alone to keep this from firing.
		if (missingLevelStudents.length > 0) {
			return;
		}
		try {
			// err-9: without .unwrap() an RTK mutation never throws, so the
			// catch below was dead code and we navigated to a questionless
			// lesson on every generation failure
			await generateQuestions({
				lessonId,
				gradeLevel: gradeBand,
				classId: selectedClassId,
				subject: subject || undefined
			}).unwrap();
			navigate(`/lesson-builder?lessonId=${lessonId}`);
		} catch (error) {
			console.error('Error generating questions:', error);
			showToast('Failed to generate questions. Please try again.');
		}
	};

	useEffect(() => {
		verifyLogin(navigate)
	}, [navigate])

	return (
		<>
			{selectedClassId && missingLevelStudents.length > 0 && (
				<MissingLevelModal classId={selectedClassId} students={missingLevelStudents} />
			)}
			<div className="upload-lessons">
				{/* <SearchBar onChange={() => {}} /> */}
				{/* mockup .page-head: eyebrow, sentence-case h1, centred rule */}
				<div className="upload-lessons-page-head">
					<span className="page-eyebrow">Start here</span>
					<div className="upload-lessons-header">
						Plan a new lesson
					</div>
					<div className="page-rule" />
				</div>
				<div className={`upload-content-row ${presentationFile ? 'has-file' : ''}`}>
					<div className="upload-box-row">
						<UploadBox
							key={uploadBoxKey}
							name="presentation documents"
							acceptedFileTypes={VALID_PRESENTATION_TYPES}
							acceptedFileExtensionsText={'.PPTX \u00b7 .PPT \u00b7 .PDF'}
							onFileUpload={(file) => handleFileUpload(file, true)}
							onDismiss={async () => {
								if (lessonId) {
									try { await deleteLesson(lessonId).unwrap() } catch (e) { /* ignore */ }
								}
								setPresentationFile(null)
								setLessonId(null)
								setSelectedClassId(null)
								setUploadBoxKey(k => k + 1)
							}}
							isUploading={isUploading}
							failed={!!uploadError}
						/>
						{/* TODO: Removed supplementary documents support for MVP */}
						{/* <UploadBox
							name="Supplementary Documents"
							isActive={!!presentationFile}
							acceptedFileTypes={VALID_SUPPLEMENTARY_TYPES}
							acceptedFileExtensionsText=".PPTX, .PPT, .DOC, .DOCX, .PDF, .TXT (Max: 2)"
							maxFiles={2}
							onFileUpload={(file) => handleFileUpload(file, false)}
							optional={true}
						/> */}
					</div>
					{presentationFile && (
					<div className="class-assignment-column">
						<div className="uploaded-file-box">
							<span className="uploaded-file-name">{presentationFile.name.replace(/\.[^/.]+$/, '')}</span>
							{isUploading ? (
								<span className="uploading-indicator">Uploading...</span>
							) : (
								<button
									type="button"
									className="dismiss-file-button"
									onClick={async () => {
										if (lessonId) {
											try { await deleteLesson(lessonId).unwrap() } catch (e) { /* ignore */ }
										}
										setPresentationFile(null)
										setLessonId(null)
										setSelectedClassId(null)
										setUploadBoxKey(k => k + 1)
									}}
									aria-label="Remove file"
								>
									×
								</button>
							)}
						</div>
						{!isUploading && !uploadError && (
						<div className="class-selection-section">
							<div className="class-selection-row">
								<label htmlFor="class-select">Assign to class:</label>
								<select
									id="class-select"
									value={selectedClassId || ''}
									onChange={(e) => handleClassSelect(e.target.value)}
									className="class-dropdown"
								>
									<option value="">Select a class...</option>
									{classes.map((c) => (
										<option key={c.classId} value={c.classId}>
											{c.name}
										</option>
									))}
								</select>
							</div>
							{selectedClassId && (
								<>
									<div className="grade-band-row">
										<span>Class is {selectedClass?.grades?.length === 1 ? 'grade' : 'grades'} {selectedClass?.grades?.join(', ') || 'N/A'}. Questions will target grades </span>
										{showGradeBandDropdown ? (
											<select
												value={gradeBand}
												onChange={(e) => {
													setGradeBand(e.target.value);
													setShowGradeBandDropdown(false);
												}}
												onBlur={() => setShowGradeBandDropdown(false)}
												autoFocus
												className="grade-band-dropdown"
											>
												<option value="K-2">K-2</option>
												<option value="3-5">3-5</option>
												<option value="6-8">6-8</option>
												<option value="9-12">9-12</option>
											</select>
										) : (
											<>
												<strong>{gradeBand}</strong>
												<button
													type="button"
													onClick={() => setShowGradeBandDropdown(true)}
													className="change-grade-link"
												>
													change
												</button>
											</>
										)}
									</div>
									<div className="grade-band-row">
										<span>Class subject is </span>
										{showSubjectDropdown ? (
											<select
												value={subject}
												onChange={(e) => {
													setSubject(e.target.value);
													setShowSubjectDropdown(false);
												}}
												onBlur={() => setShowSubjectDropdown(false)}
												autoFocus
												className="grade-band-dropdown"
											>
												<option value="Science">Science</option>
												<option value="Math">Math</option>
												<option value="ELA">ELA</option>
												<option value="Social Studies">Social Studies</option>
												<option value="Other">Other</option>
											</select>
										) : (
											<>
												<strong>{subject || 'General'}</strong>
												<button
													type="button"
													onClick={() => setShowSubjectDropdown(true)}
													className="change-grade-link"
												>
													change
												</button>
											</>
										)}
									</div>
									{selectedClass && (
										<ClassContextProvider initialClass={selectedClass}>
											<div className="class-info-section">
												<DifficultyPills currentDifficulty={currentTableDifficulty} handleDifficultyChange={setCurrentTableDifficulty} />
												<div className="student-table-container">
													<StudentTable
														keyFilter={new Set(['difficulty', 'studentName', 'grade'])}
														style={{}}
														currentDifficulty={currentTableDifficulty}
														renderHeader={true}
													/>
												</div>
											</div>
										</ClassContextProvider>
									)}
								</>
							)}
							<div className="alignRight">
								<DoneButton onClick={handleDoneClick} disabled={!selectedClassId || missingLevelStudents.length > 0} />
							</div>
						</div>
						)}
					</div>
				)}
				</div>
				{uploadError && (
					<div className="error-message">
						{JSON.stringify(uploadError)}
					</div>
				)}
				{isGenerating && (
					<GeneratingModal fileName={presentationFile.name.replace(/\.[^/.]+$/, '')} />
				)}
			</div>
		</>
	);
};

